import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import jwt_decode from "jwt-decode";
import * as XLSX from 'xlsx';

import Navbar from '../../Navbar';
import Header from '../../Header';
import Loader from '../../Loader';
import baseUrl from '../../BaseUrl';
import Pagination from "react-js-pagination";
// import { mapDbStatus, determineTrend, calculateRiskTrend, calculateCarrierChange } from './trendCalculations';

const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

const ENRMSDetailComparison = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { compid, batches, persistedState, tags, tagOperator } = location.state || {};

    const [name, setName] = useState('');
    const [token, setToken] = useState('');
    const [expire, setExpire] = useState('');
    const [access, setAccess] = useState([]);
    const [popupOpen, setPopupOpen] = useState(false);

    const [rawData, setRawData] = useState(null);
    const [uniqueCarrierPlatforms, setUniqueCarrierPlatforms] = useState([]);
    const [mostRecentBatchId, setMostRecentBatchId] = useState(null);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [totalItem, setTotalItem] = useState(0);
    const [tableData, setTableData] = useState([]);

    useEffect(() => {
        refreshToken();
        getUserAccess();
    }, []);

    useEffect(() => {
        if (token && compid && batches) {
            fetchDetailData();
        } else if (!location.state) {
            navigate('/enrms-summary-comparison');
        }
    }, [token, compid, batches, page, filters]);

    const refreshToken = async () => {
        try {
            const response = await axios.get(`${baseUrl}/token`);
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setExpire(decoded.exp);
        } catch (error) {
            if (error.response) navigate("/");
        }
    };

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        const currentDate = new Date();
        if (expire * 1000 < currentDate.getTime()) {
            const response = await axios.get(`${baseUrl}/token`);
            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
            setToken(response.data.accessToken);
            const decoded = jwt_decode(response.data.accessToken);
            setName(decoded.name);
            setExpire(decoded.exp);
        }
        return config;
    }, (error) => Promise.reject(error));

    const getUserAccess = async () => {
        try {
            const response = await axiosJWT.get(`${baseUrl}/user-access`, { headers: { Authorization: `Bearer ${token}` } });
            setAccess(response.data.results);
        } catch (error) {
            console.log(error);
        }
    };

    const fetchDetailData = async () => {
        setPopupOpen(true);
        try {
            const response = await axiosJWT.post(`${baseUrl}/get-enrms-detail-comparison`, { 
                batches,
                filters,
                page: page,
                limit: 50,
                tags,
                tagOperator }, { headers: { Authorization: `Bearer ${token}` } });
                setTableData(response.data.data);
            // setRawData(response.data.data);
            setUniqueCarrierPlatforms(response.data.uniqueCarrierPlatforms);
            setMostRecentBatchId(response.data.mostRecentBatchId);
            setTotalItem(response.data.totalItems);
        } catch (error) {
            console.error("Error fetching detail comparison data:", error);
            alert("Failed to fetch detail comparison data.");
        } finally {
            setPopupOpen(false);
        }
    };

    // const processedData = useMemo(() => {
    //     if (!rawData || !mostRecentBatchId) return [];
    //     return Object.keys(rawData).map(tn_number => {
    //         const history = rawData[tn_number];
    //         const { riskChange, riskLevelTrend } = calculateRiskTrend(history, mostRecentBatchId);
    //         const carrierChange = calculateCarrierChange(history);

    //         const row = {
    //             tn_number,
    //             riskChange,
    //             riskLevelTrend,
    //             carrierChange,
    //         };

    //         uniqueCarrierPlatforms.forEach(platformKey => {
    //             const [carrierName, platform] = platformKey.split('_');
    //             const recordsForCarrier = history.filter(r => r.carrier_name === carrierName);
    //             let statusHistory;
    //             if (platform === 'Android') {
    //                 statusHistory = recordsForCarrier.map(r => ({ batch_id: r.batch_id, status: mapDbStatus(r.spam_status_android) }));
    //             } else { // iOS
    //                 statusHistory = recordsForCarrier.map(r => ({ batch_id: r.batch_id, status: mapDbStatus(r.spam_status_ios) }));
    //             }
    //             row[platformKey] = determineTrend(statusHistory, mostRecentBatchId);
    //         });

    //         return row;
    //     });
    // }, [rawData, mostRecentBatchId, uniqueCarrierPlatforms]);

    // const filteredData = useMemo(() => {
    //     if (Object.keys(filters).length === 0) return processedData;

    //     return processedData.filter(row => {
    //         return Object.keys(filters).every(filterKey => {
    //             const filterValue = filters[filterKey];
    //             if (!filterValue) return true; // No filter set for this key
    //             return row[filterKey] === filterValue;
    //         });
    //     });
    // }, [processedData, filters]);

    const handleFilterChange = (key, value) => {
        // setFilters(prev => ({ ...prev, [key]: value }));
        setFilters(prev => {
            const newFilters = { ...prev };
            if (key === 'commonStatus') {
                newFilters.commonStatus = value;
                // Also set all individual carrier/platform filters to the same value
                uniqueCarrierPlatforms.forEach(platformKey => {
                    newFilters[platformKey] = "";
                });
            } else {
                // This will only be reached for non-carrier filters when a common filter is active,
                // because the carrier filters are disabled.
                delete newFilters.commonStatus;
                newFilters[key] = value;
            }
            return newFilters;
        });
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({});
        setPage(1);
    };

    const goBack = () => {
        navigate('/enrms-summary-comparison', { state: { persistedState, compid } });
    };

    const FilterDropdown = ({ columnKey, options, disabled = false }) => (
        <select
            onChange={(e) => handleFilterChange(columnKey, e.target.value)}
            value={filters[columnKey] || ''}
            disabled={disabled}
            style={{ backgroundColor: disabled ? '#f2f2f2' : 'white' }}
        >
            <option value="">All</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    );

    const exportHeaders = useMemo(() => [
        { label: "Number", key: "tn_number" },
        { label: "Risk Change", key: "riskChange" },
        { label: "Scan Risk Level", key: "riskLevelTrend" },
        { label: "Carrier Change", key: "carrierChange" },
        ...uniqueCarrierPlatforms.map(key => ({ label: key.replace(/_/g, ' '), key: key }))
    ], [uniqueCarrierPlatforms]);

    // const handleExport = () => {
    //     // Transform data to use the 'label' from headers as the key for each cell
    //     const dataToExport = filteredData.map(row => {
    //         const newRow = {};
    //         exportHeaders.forEach(header => {
    //             newRow[header.label] = row[header.key];
    //         });
    //         return newRow;
    //     });

    //     // Create a worksheet from the transformed data
    //     const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    //     const workbook = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(workbook, worksheet, "Detail Comparison");

    //     // Trigger the file download
    //     XLSX.writeFile(workbook, "detail_comparison_export.xlsx");
    // };
    const handleExport = async () => {
        setPopupOpen(true);
        try {
            const response = await axiosJWT.post(`${baseUrl}/get-enrms-detail-comparison`, {
                batches,
                filters,
                is_export: true,
                tags,
                tagOperator
            }, { headers: { Authorization: `Bearer ${token}` } });

            const dataForExport = response.data.data;

            // Transform data to use the 'label' from headers as the key for each cell
            const transformedData = dataForExport.map(row => {
                const newRow = {};
                exportHeaders.forEach(header => {
                    newRow[header.label] = row[header.key];
                });
                return newRow;
            });

            const worksheet = XLSX.utils.json_to_sheet(transformedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Detail Comparison");
            XLSX.writeFile(workbook, "detail_comparison_export.xlsx");
        } catch (error) {
            console.error("Error exporting data:", error);
            alert("Failed to export data.");
        } finally {
            setPopupOpen(false);
        }
    };

    const handlePageChange  = (pageNumber) => {
        // console.log(`active page is ${pageNumber}`);
        setPage(pageNumber);
    }

    return (
        <div className="panelbox">
            <Navbar token={token} access={access} />
            <div className="right-panel">
                <Header name={name} />
                {popupOpen && <Loader />}
                <div className="content-page admin-attendance-page">
                    <div className="row">
                        <div className="col-8">
                            <div className="left-title">
                                <h1>
                                E-NRMS Detail Comparison
                                  <span>
                                    {persistedState?.displayDate && persistedState?.displayEndDate && (
                                      <>
                                        ({formatDate(persistedState.displayDate)} - {formatDate(persistedState.displayEndDate)})
                                      </>
                                    )}
                                    {tags && tags.length > 0 && (
                                        <>
                                            <br />
                                            Tags: {tags.map(t => t.label).join(', ')} ({tagOperator})
                                        </>
                                    )}
                                  </span>
                                </h1>                                
                            </div>
                        </div>
                        <div className="col-4 inventory-btn">
                            <div className="addnew-button">
                            {Object.values(filters).some(v => v) && (
                                <a
                                    href="#"
                                    className="newrow"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        clearFilters();
                                    }}
                                    style={{ textDecoration: 'none', color: 'white', marginRight: '10px' }}
                                >
                                    Clear Filters
                                </a>
                            )}
                            {tableData.length > 0 && (
                                    <a
                                        href="#"
                                        className="newrow"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleExport();
                                        }}
                                        style={{textDecoration: 'none', color: 'white', marginRight: '10px'}}
                                    >
                                        Export
                                    </a>
                                )}
                                <a href="#" onClick={goBack}>Back</a>
                            </div>
                        </div>
                    </div>

                    {/* {processedData.length > 0 ? ( */}
                        <div className="common-white-shadow-background setting-section">
                        <div className="row" style={{ alignItems: 'center', marginBottom: '1rem', padding: '10px' }}>
                            <div className="col-md-2">
                                <label style={{ fontWeight: 'bold' }}>Status Filter:</label>
                            </div>
                            <div className="col-md-3">
                                <FilterDropdown
                                    columnKey="commonStatus"
                                    options={['All Normal', 'Improved', 'Declined', 'Mult Spam', 'Mult Unknown', 'No Data']}
                                />
                            </div>
                            <div className="col-md-7">
                                <small>Filters numbers where <strong>any</strong> carrier/platform matches the selected status.</small>
                            </div>
                        </div>
                            <h3>Comparison Details</h3>
                            <div className="setting-section haft-whitebox cia-table" style={{ overflowX: 'auto' }}>
                                <table className="tablebox search-result-table inventory-table">
                                    <thead>
                                        <tr>
                                            <th>Number</th>
                                            <th>
                                                Risk Change
                                                <FilterDropdown columnKey="riskChange" options={['Changed', 'No Change']} />
                                            </th>
                                            <th>
                                                Scan Risk Level
                                                <FilterDropdown columnKey="riskLevelTrend" options={['All 0', 'Improved', 'Increased Risk', 'Issue - Risk', 'Unspecified']} />
                                            </th>
                                            <th>
                                                Carrier Change
                                                <FilterDropdown columnKey="carrierChange" options={['Changed', 'No Change']} />
                                            </th>
                                            {uniqueCarrierPlatforms.map(key => (
                                            <th key={key}>
                                                {key.replace(/_/g, ' ')}
                                                <FilterDropdown
                                                    columnKey={key}
                                                    options={['All Normal', 'Improved', 'Declined', 'Mult Spam', 'Mult Unknown', 'No Data']}
                                                    disabled={!!filters.commonStatus}
                                                />
                                            </th>
                                        ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.map(row => (
                                            <tr key={row.tn_number}>
                                                <td><strong>{row.tn_number}</strong></td>
                                                <td>{row.riskChange}</td>
                                                <td>{row.riskLevelTrend}</td>
                                                <td>{row.carrierChange}</td>
                                                {uniqueCarrierPlatforms.map(key => (
                                                    <td key={`${row.tn_number}-${key}`}>{row[key]}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    {/* ) : !popupOpen && ( */}
                        {tableData.length == 0 && !popupOpen &&<div className="common-white-shadow-background setting-section">
                            <p>No data available for detail comparison.</p>
                        </div>}
                    {/* )} */}

                    {totalItem > 50 && (<Pagination
                      activePage={page}
                      itemsCountPerPage={50}
                      totalItemsCount={totalItem}
                      pageRangeDisplayed={5}
                      onChange={handlePageChange.bind(this)}
                    />)}
                </div>
            </div>
        </div>
    );
};

export default ENRMSDetailComparison;
